import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PMActivityFeed, PMAvatar, PMButton, PMCalendar, PMCheckbox, PMIcon, PMWidget, PMWidgetBoard } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useLocations, usePeople, useReminders, useVisits, type Visit, type Reminder } from "@/data/queries";
import { useMutations } from "@/data/mutations";
import { FieldVisitCard } from "@/ui/FieldVisitCard";
import { VisitStatusBadge } from "@/ui/visit";
import { visitStatus } from "@/ui/visitStatus";
import { CountUp } from "@/ui/motion";
import { LocationMap } from "@/ui/LocationMap";
import { ConditionsRow } from "@/ui/Conditions";
import { Character } from "@/ui/Character";
import { HomeHero } from "./HomeHero";
import { usePlayer } from "@/ui/player";
import { useQuery } from "@powersync/react";
import { avatarColor, clock, dayKey, fullName, initials, relTime } from "@/ui/format";
import "./home.css";

const DAY = 86_400_000;

// Home = the DS desktop-home template (PMPageHeader + PMWidgetBoard + PMActivityFeed),
// extended for advisors: sign-off queue with photos, live field activity, farm
// conditions with spray windows, and a tasks ring that Happy Propal cheers on.
export function HomePage() {
  const { teamId, hierarchy, userId } = useApp();
  const nav = useNavigate();
  const visits = useVisits();
  const tasks = useReminders();
  const people = usePeople();
  const top = hierarchy.levels[0];
  const bottom = hierarchy.levels.at(-1)!;
  const farms = useLocations(top.key);
  const fields = useLocations(bottom.key);
  const m = useMutations();
  const [editing, setEditing] = useState(false);
  const player = usePlayer();
  const preps = useQuery<{ id: string; summary_text: string | null; map_feature_id: string; label: string; created_at: string; viewed: number | null }>(
    `SELECT p.id, p.summary_text, p.map_feature_id, mf.label, p.created_at, p.viewed FROM feature_prep_notes p JOIN map_features mf ON mf.id = p.map_feature_id
     JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? ORDER BY p.created_at DESC LIMIT 6`, [teamId]).data;

  const me = people.data.find((p) => p.user_id === userId);
  const person = (id?: string | null) => people.data.find((p) => p.user_id === id);
  const open = (v: Visit) => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: v.id } });
  const today = dayKey(new Date().toISOString());

  const review = visits.data.filter((v) => ["review", "failed"].includes(visitStatus(v)));
  const live = visits.data.filter((v) => ["in_progress", "processing"].includes(visitStatus(v)));
  const week = visits.data.filter((v) => Date.now() - new Date(v.started_at || 0).getTime() < 7 * DAY);
  const openTasks = tasks.data.filter((t) => !t.completed_at && !t.canceled_at);
  const mine = tasks.data.filter((t) => !t.assigned_user_id || t.assigned_user_id === userId);
  const dueSoon = mine.filter((t) => !t.completed_at && !t.canceled_at && (t.reminder_date || "") <= dayKey(new Date(Date.now() + 2 * DAY).toISOString()));
  const doneToday = mine.filter((t) => t.completed_at && dayKey(t.completed_at) === today);



  const events = [
    ...visits.data.slice(0, 120).map((v) => ({ id: v.id, date: dayKey(v.started_at), type: "visit", title: v.location_label || "Field visit", subtitle: fullName(person(v.user_id)), time: clock(v.started_at) })),
    ...openTasks.map((t) => ({ id: t.id, date: t.reminder_date || today, type: "task", title: t.details || "Task", subtitle: t.location_label ?? undefined, time: t.reminder_time?.slice(0, 5) })),
  ];

  const feed = useMemo(() => visits.data.slice(0, 7).map((v) => {
    const p = person(v.user_id);
    const s = visitStatus(v);
    const what = s === "signed" ? "signed the report for" : s === "in_progress" ? "is walking" : s === "processing" ? "just finished" : "completed a field visit —";
    return {
      id: v.id,
      avatar: { initials: initials(fullName(p)), color: avatarColor(v.user_id) },
      text: <span><strong>{fullName(p)}</strong> {what} <a href={`/t/${teamId}/visits/${v.id}`} onClick={(e) => { e.preventDefault(); open(v); }}><strong>{v.location_label}</strong></a>{v.obs_count ? <span className="subtle"> · {v.obs_count} observations</span> : null}</span>,
      time: relTime(v.report_approved_at || v.completed_at || v.started_at),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [visits.data, people.data]);

  const lastVisit = new Map<string, string>();
  for (const v of visits.data) if (v.map_feature_id && !lastVisit.has(v.map_feature_id)) lastVisit.set(v.map_feature_id, v.started_at || "");
  const coverage = fields.data.map((p) => {
    const last = lastVisit.get(p.id) || p.last_visit;
    const age = last ? (Date.now() - new Date(last).getTime()) / DAY : 999;
    return { id: p.id, label: p.label || "", geometry: p.geometry, latitude: p.latitude, longitude: p.longitude, tone: age <= 7 ? "primary" as const : age <= 21 ? "warning" as const : "audio" as const, sub: last ? `Visited ${relTime(last)}` : "Never visited" };
  });

  const complete = (t: Reminder) => m.completeReminder(t.id, Math.max(0, dueSoon.filter((x) => x.id !== t.id && (x.reminder_date || "") <= today).length));
  const totalToday = dueSoon.length + doneToday.length;

  return (
    <div className="page page--wide home">
      <HomeHero name={me?.name ?? "there"} review={review} due={dueSoon} live={live} farms={farms.data} fields={fields.data} />
      <div className="home-bar">
        <h2 className="home-h2">Your board</h2>
        <PMButton variant={editing ? "go" : "text"} size="sm" icon={editing ? "check-circle" : "settings"} onClick={() => setEditing((e) => !e)}>{editing ? "Done" : "Customize"}</PMButton>
      </div>

      <PMWidgetBoard customizing={editing} storageKey={`pm.home.v2.${teamId}`} role="admin">
        <PMWidget widgetId="actions" pinned showEyebrow={false}>
          <div className="jump">
            <button onClick={() => nav({ to: "/t/$teamId/visits", params: { teamId }, search: { status: "review" } })}><PMIcon name="document-outline" size={20} />Sign reports{review.length ? <span className="jump-count">{review.length}</span> : null}</button>
            <button onClick={() => nav({ to: "/t/$teamId/map", params: { teamId } })}><PMIcon name="map-outline" size={20} />Open map</button>
            <button onClick={() => nav({ to: "/t/$teamId/locations", params: { teamId } })}><PMIcon name="map-pin-outline" size={20} />View {top.plural.toLowerCase()}</button>
            <button onClick={() => nav({ to: "/t/$teamId/tasks", params: { teamId } })}><PMIcon name="add-circle-outline" size={20} />Add a task</button>
          </div>
        </PMWidget>

        <PMWidget widgetId="tasks" title="Tasks assigned to me" icon="check-circle" count={dueSoon.length}
          headerLink={{ label: "All tasks", onClick: () => nav({ to: "/t/$teamId/tasks", params: { teamId } }) }}
          variants={[
            { id: "a", label: "Checklist", icon: "columns", render: () => dueSoon.length ? (
              <ul className="checklist">
                {dueSoon.slice(0, 5).map((t) => (
                  <li key={t.id} className={(t.reminder_date || "") < today ? "is-late" : ""}>
                    <PMCheckbox checked={false} onChange={() => complete(t)} aria-label={`Complete ${t.details}`} />
                    <button className="checklist-t checklist-open" onClick={() => nav({ to: "/t/$teamId/tasks", params: { teamId }, search: { task: t.id } } as any)}>{(t.details || "").split(/\n/)[0]}<small>{t.location_label}</small></button>
                    <span className="checklist-due">{(t.reminder_date || "") < today ? "Overdue" : t.reminder_date === today ? "Due today" : "Tomorrow"}</span>
                  </li>
                ))}
              </ul>
            ) : <Character name="happy-propal" size={84} title="All clear" line={doneToday.length ? `${doneToday.length} done today` : "Nothing due in the next two days"} /> },
            { id: "b", label: "Progress", icon: "bar-chart", render: () => (
              <div className="ring">
                <div className="ring-r" style={{ ["--p" as any]: `${totalToday ? (doneToday.length / totalToday) * 100 : 100}%` }}><b>{doneToday.length}/{totalToday || 0}</b></div>
                <div><div className="ring-h">{doneToday.length} of {totalToday} done</div><div className="hint">{dueSoon.length} still open · {dueSoon.filter((t) => t.reminder_date === today).length} due today</div></div>
              </div>
            ) },
          ]} />

        <PMWidget widgetId="prep" span={2} title="Prep briefings" icon="play" count={preps.length}>
          {preps.length ? (
            <div className="prep-grid">
              {preps.map((p) => {
                const on = player.current?.id === p.id;
                return (
                  <div key={p.id} className={`prep ${on ? "is-on" : ""}`}>
                    <button className="prep-play" aria-label={`Play briefing for ${p.label}`} onClick={() => player.play({ id: p.id, title: p.label, sub: "Pre-visit briefing", text: p.summary_text || "", locationId: p.map_feature_id })}>
                      {on && player.playing ? <span className="player-pause" aria-hidden="true"><i /><i /></span> : <PMIcon name="play-filled" size={20} fg="currentColor" accent="currentColor" />}
                    </button>
                    <div className="list-main">
                      <div className="list-title">{p.label}{!p.viewed && <span className="prep-new">New</span>}</div>
                      <p className="prep-text clamp-2">{p.summary_text}</p>
                      <span className="hint">{Math.max(1, Math.round((p.summary_text || "").split(/\s+/).length / 155))} min listen · {relTime(p.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Character name="farmer" size={90} title="No briefings yet" line="Briefings are generated before scheduled visits." />}
        </PMWidget>

        <PMWidget widgetId="market" title="Market data" icon="bar-chart">
          <div className="market">
            <p className="hint" style={{ margin: 0 }}>National and local grain prices come from the classic market feed (Databento and USDA NASS). They appear here once the web API connects.</p>
            <a className="linkbtn" href="https://dev.propelmapper.com" target="_blank" rel="noopener">Open in classic</a>
          </div>
        </PMWidget>

        <PMWidget widgetId="review" span={2} title="Ready to sign" icon="document" count={review.length}
          headerLink={review.length ? { label: "View all", onClick: () => nav({ to: "/t/$teamId/visits", params: { teamId }, search: { status: "review" } }) } : null}>
          {review.length ? (
            <div className="review-row">{review.slice(0, 3).map((v) => <FieldVisitCard key={v.id} v={v} compact onOpen={() => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: v.id }, search: { tab: "report" } })} />)}</div>
          ) : <Character name="propal" size={90} title="Every report is signed" line="New drafts appear here as visits finish processing." />}
        </PMWidget>

        <PMWidget widgetId="live" title="In the field" icon="tractor" count={live.length}>
          {live.length ? (
            <div className="hub">
              {live.map((v) => (
                <button key={v.id} className="hub-active" onClick={() => open(v)}>
                  <span className={`hub-dot ${visitStatus(v) === "processing" ? "hub-dot--proc" : ""}`}><i /></span>
                  <span className="list-main"><span className="list-title">{v.location_label}</span><span className="meta">{fullName(person(v.user_id))}<span className="sep" />{v.obs_count} obs<span className="sep" />{relTime(v.started_at)}</span></span>
                  <VisitStatusBadge v={v} />
                </button>
              ))}
            </div>
          ) : <Character name="car" size={120} title="Nobody in the field" line="Open visits show here live as phones sync." />}
        </PMWidget>

        <PMWidget widgetId="week" title="This week" icon="bar-chart"
          variants={[
            { id: "a", label: "Totals", icon: "columns", render: () => (
              <div className="minis">
                <Mini emoji="📋" tone="tone-brand" v={week.length} l="Field visits" />
                <Mini emoji="📷" tone="tone-photo" v={week.reduce((a, v) => a + (v.photo_count ?? 0), 0)} l="Photos" />
                <Mini emoji="🎙️" tone="tone-audio" v={week.reduce((a, v) => a + (v.audio_count ?? 0), 0)} l="Voice memos" />
                <Mini emoji={bottom.emoji} tone="tone-text" v={new Set(week.map((v) => v.map_feature_id)).size} l={`${bottom.plural} walked`} />
              </div>
            ) },
            { id: "b", label: "By day", icon: "bar-chart", render: () => <WeekBars visits={visits.data} /> },
          ]} />

        <PMWidget widgetId="conditions" span={2} title="Field conditions" icon="leaf"
          headerLink={{ label: `All ${top.plural.toLowerCase()}`, onClick: () => nav({ to: "/t/$teamId/locations", params: { teamId } }) }}>
          <div className="cond-list">
            {farms.data.filter((f) => f.latitude && f.longitude).slice(0, 6).map((f) => (
              <ConditionsRow key={f.id} label={f.label || ""} emoji={top.emoji} lat={+f.latitude!} lng={+f.longitude!} onOpen={() => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: f.id } })} />
            ))}
            {!farms.data.some((f) => f.latitude) && <p className="hint">Conditions appear once {top.plural.toLowerCase()} have a map location.</p>}
          </div>
        </PMWidget>

        <PMWidget widgetId="coverage" span={2} title="Coverage" icon="map" headerLink={{ label: "Open map", onClick: () => nav({ to: "/t/$teamId/map", params: { teamId } }) }}>
          <div className="coverage">
            <LocationMap items={coverage} height={320} onSelect={(id) => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: id } })} />
            <div className="map-legend pm-glass"><span><i className="lg-g" />This week</span><span><i className="lg-a" />1–3 weeks</span><span><i className="lg-r" />Overdue</span></div>
          </div>
        </PMWidget>

        <PMWidget widgetId="activity" title="Recent activity" icon="chat-dots" showEyebrow={false}>
          <PMActivityFeed title="Recent activity" items={feed}
            activePeople={{ avatars: people.data.slice(0, 5).map((p) => ({ initials: initials(fullName(p)), color: avatarColor(p.user_id), name: fullName(p) })), label: `${people.data.length} people on ${hierarchy.levels[0].plural.toLowerCase()} this week` }} />
        </PMWidget>

        <PMWidget widgetId="calendar" span={2} title="Calendar" icon="clock" showEyebrow={false}>
          <PMCalendar events={events} title="Calendar" categories={{ visit: { label: "Visit", color: "var(--pm-primary)" }, task: { label: "Task", color: "var(--pm-info)" } }}
            onEventClick={(e: any) => (e.type === "visit" ? nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: e.id } }) : nav({ to: "/t/$teamId/tasks", params: { teamId } }))}
            headerLink={{ label: "Open tasks", onClick: () => nav({ to: "/t/$teamId/tasks", params: { teamId } }) }} />
        </PMWidget>

        <PMWidget widgetId="team" span={2} adminOnly title="Team this week" icon="user" headerLink={{ label: "Members", onClick: () => nav({ to: "/t/$teamId/settings", params: { teamId }, search: { section: "members" } }) }}>
          <TeamTable people={people.data} visits={week} />
        </PMWidget>
      </PMWidgetBoard>
    </div>
  );
}

function Mini({ emoji, tone, v, l }: { emoji: string; tone: string; v: number; l: string }) {
  return <div className="mini"><span className={`stat-ic ${tone}`}>{emoji}</span><div><div className="stat-val"><CountUp value={v} /></div><div className="stat-lbl">{l}</div></div></div>;
}

function WeekBars({ visits }: { visits: Visit[] }) {
  const days = Array.from({ length: 7 }, (_, i) => dayKey(new Date(Date.now() - (6 - i) * DAY).toISOString()));
  const counts = days.map((d) => visits.filter((v) => dayKey(v.started_at) === d).length);
  const max = Math.max(1, ...counts);
  return (
    <div className="weekbars" role="img" aria-label={`Visits per day: ${counts.join(", ")}`}>
      {days.map((d, i) => (
        <div key={d} className="weekbar"><span className="weekbar-n">{counts[i] || ""}</span><i style={{ height: `${(counts[i] / max) * 100}%` }} className={i === 6 ? "is-today" : ""} /><span className="weekbar-d">{new Date(d + "T12:00").toLocaleDateString(undefined, { weekday: "narrow" })}</span></div>
      ))}
    </div>
  );
}

function TeamTable({ people, visits }: { people: { user_id: string; name: string | null; last_name: string | null }[]; visits: Visit[] }) {
  const rows = people.map((p) => ({ p, n: visits.filter((v) => v.user_id === p.user_id).length, signed: visits.filter((v) => v.user_id === p.user_id && v.report_approved_at).length })).sort((a, b) => b.n - a.n);
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <table className="atab">
      <thead><tr><th>Advisor</th><th>Visits</th><th>Signed</th><th style={{ width: "40%" }}>This week</th></tr></thead>
      <tbody>{rows.map((r) => <tr key={r.p.user_id}><td><span className="row"><PMAvatar name={fullName(r.p)} size={26} color={avatarColor(r.p.user_id)} />{fullName(r.p)}</span></td><td>{r.n}</td><td>{r.signed}</td><td><div className="atab-bar" style={{ width: `${(r.n / max) * 100}%` }} /></td></tr>)}</tbody>
    </table>
  );
}
